import { useEffect, useState } from 'react'
import { ArrowDown, ArrowUp, Check, Pencil, Plus, Trash2, X } from 'lucide-react'
import { isManager, useAuth } from '@/contexts/AuthContext'
import { useLanguage } from '@/contexts/LanguageContext'
import EmojiGlyph from '@/components/ui/EmojiGlyph'
import { invalidateMatchSpecs } from '@/lib/matchSpecs'
import EmojiPicker from '@/components/settings/EmojiPicker'
import SettingsSection, {
  SettingsEmpty, SettingsSkeleton,
  settingsAddButton, settingsAddRow, settingsIconButton, settingsInput, settingsRow,
} from '@/components/settings/SettingsSection'

/** Mirrors MatchClass.kind in the backend. */
export type ClassKind = 'primary' | 'bench' | 'tentative' | 'out'

/** A sub-class offered once its position is picked ("Air Force" → Havoc). */
interface Spec {
  name: string
  emoji: string
}

interface MatchClass {
  id: number
  name: string
  /** A unicode emoji, or a guild one as Discord's own `<:name:id>` text. */
  emoji: string
  kind: ClassKind
  sort_order: number
  specs: Spec[]
}

const KINDS: ClassKind[] = ['primary', 'bench', 'tentative', 'out']
// Discord's cap on a select menu: the sub-class menu must list them all.
const MAX_SPECS = 25

/** The sub-class list of the position being edited.
 *
 *  Its own component, outside MatchClassSettings: defined inline it would be a
 *  new component type on every keystroke and the input would lose focus. */
function SpecEditor({ specs, onChange }: { specs: Spec[]; onChange: (s: Spec[]) => void }) {
  const { T } = useLanguage()
  const [name, setName] = useState('')
  const [emoji, setEmoji] = useState('')

  const clean = name.trim()
  const taken = specs.some(s => s.name.toLowerCase() === clean.toLowerCase())
  const add = () => {
    if (!clean || taken || specs.length >= MAX_SPECS) return
    onChange([...specs, { name: clean, emoji: emoji.trim() }])
    setName(''); setEmoji('')
  }

  return (
    <div className="ml-14 space-y-2 border-l border-white/10 pb-1 pl-3">
      <div>
        <p className="font-display text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70">{T.settings.classSpecs}</p>
        <p className="text-[11px] text-muted-foreground/50">{T.settings.classSpecsHint}</p>
      </div>
      {specs.length === 0 && <p className="text-[11px] italic text-muted-foreground/40">{T.settings.noClassSpecs}</p>}
      {specs.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {specs.map(sp => (
            <span key={sp.name} className="flex items-center gap-1 rounded-sm border border-white/10 bg-white/5 py-0.5 pl-1.5 pr-0.5 text-xs">
              {sp.emoji && <EmojiGlyph emoji={sp.emoji} size={14} />}
              {sp.name}
              <button
                type="button"
                onClick={() => onChange(specs.filter(x => x.name !== sp.name))}
                title={T.settings.removeSpec}
                className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground/50 hover:bg-white/10 hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      {specs.length < MAX_SPECS && (
        <div className="flex items-center gap-2">
          <EmojiPicker value={emoji} onChange={setEmoji} />
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            // Enter adds the sub-class here; it must not also save the position.
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); add() } }}
            placeholder={T.settings.newSpecName}
            className={`${settingsInput} min-w-0 flex-1 sm:max-w-56`}
          />
          <button type="button" onClick={add} disabled={!clean || taken} className={settingsIconButton}>
            <Plus className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  )
}

export default function MatchClassSettings() {
  const { user } = useAuth()
  const { T } = useLanguage()
  const canEdit = isManager(user)

  const [classes, setClasses] = useState<MatchClass[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [editingId, setEditingId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')
  const [editEmoji, setEditEmoji] = useState('')
  const [editKind, setEditKind] = useState<ClassKind>('primary')
  const [editSpecs, setEditSpecs] = useState<Spec[]>([])

  const [newName, setNewName] = useState('')
  const [newEmoji, setNewEmoji] = useState('')
  const [newKind, setNewKind] = useState<ClassKind>('primary')
  const [busy, setBusy] = useState(false)

  const kindLabel: Record<ClassKind, string> = {
    primary: T.settings.classKindPrimary,
    bench: T.settings.classKindBench,
    tentative: T.settings.classKindTentative,
    out: T.settings.classKindOut,
  }

  const fetchClasses = () => {
    invalidateMatchSpecs()  // the roster builder reads the same list
    setLoading(true)
    fetch('/api/match-classes', { credentials: 'include' })
      .then(r => (r.ok ? r.json() : []))
      .then(setClasses)
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  useEffect(fetchClasses, [])

  /** Runs a mutation, surfacing the server's own reason on failure. */
  const send = async (url: string, method: string, body?: unknown) => {
    setBusy(true)
    setError(null)
    try {
      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        body: body ? JSON.stringify(body) : undefined,
      })
      if (!res.ok) {
        const d = await res.json().catch(() => ({}))
        setError(d.detail ?? T.settings.classSaveError)
        return false
      }
      fetchClasses()
      return true
    } catch {
      setError(T.settings.classSaveError)
      return false
    } finally {
      setBusy(false)
    }
  }

  const handleCreate = async () => {
    if (!newName.trim()) return
    const ok = await send('/api/match-classes', 'POST', {
      name: newName.trim(), emoji: newEmoji.trim(), kind: newKind,
    })
    if (ok) { setNewName(''); setNewEmoji(''); setNewKind('primary') }
  }

  const handleUpdate = async (id: number) => {
    if (!editName.trim()) return
    const ok = await send(`/api/match-classes/${id}`, 'PUT', {
      name: editName.trim(), emoji: editEmoji.trim(), kind: editKind, specs: editSpecs,
    })
    if (ok) setEditingId(null)
  }

  const move = (index: number, delta: number) => {
    const target = index + delta
    if (target < 0 || target >= classes.length) return
    const next = [...classes]
    ;[next[index], next[target]] = [next[target], next[index]]
    setClasses(next)  // optimistic: the list reorders under the cursor at once
    send('/api/match-classes/order', 'PUT', { ids: next.map(c => c.id) })
  }

  const startEdit = (c: MatchClass) => {
    setEditingId(c.id)
    setEditName(c.name)
    setEditEmoji(c.emoji)
    setEditKind(c.kind)
    setEditSpecs(c.specs ?? [])
  }

  /** Three mutually exclusive meanings, so a segmented control rather than a select. */
  const KindPicker = ({ value, onChange }: { value: ClassKind; onChange: (k: ClassKind) => void }) => (
    <div className="flex shrink-0 items-center rounded border border-white/10 bg-white/5 p-0.5">
      {KINDS.map(k => (
        <button
          key={k}
          type="button"
          onClick={() => onChange(k)}
          className={`h-7 rounded-sm px-2 text-[11px] font-medium transition-colors ${
            value === k
              ? 'bg-primary/20 text-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {kindLabel[k]}
        </button>
      ))}
    </div>
  )

  /** The kind a row carries, as a quiet tag — only "playing" counts in a head-count. */
  const KindTag = ({ kind }: { kind: ClassKind }) => (
    <span className={`shrink-0 rounded-sm border px-1.5 py-0.5 text-[10px] font-medium ${
      kind === 'primary'
        ? 'border-primary/30 bg-primary/10 text-primary'
        : 'border-white/10 text-muted-foreground/70'
    }`}>
      {kindLabel[kind]}
    </span>
  )

  return (
    <SettingsSection title={T.settings.matchClasses} description={T.settings.matchClassesDesc}>
      {loading ? <SettingsSkeleton rows={4} /> : (
        <div className="space-y-2">
          {classes.map((c, i) => (
            <div key={c.id} className="space-y-2">
            <div className={settingsRow}>
              {editingId === c.id ? (
                <>
                  <EmojiPicker value={editEmoji} onChange={setEditEmoji} />
                  <input
                    type="text"
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleUpdate(c.id); if (e.key === 'Escape') setEditingId(null) }}
                    className={`${settingsInput} min-w-0 flex-1`}
                    autoFocus
                  />
                  <KindPicker value={editKind} onChange={setEditKind} />
                  <button onClick={() => handleUpdate(c.id)} disabled={busy} className={`${settingsIconButton} hover:text-green-400`}>
                    <Check className="h-4 w-4" />
                  </button>
                  <button onClick={() => setEditingId(null)} className={settingsIconButton}>
                    <X className="h-4 w-4" />
                  </button>
                </>
              ) : (
                <>
                  <span className="flex h-8 w-11 shrink-0 items-center justify-center">
                    <EmojiGlyph emoji={c.emoji} size={18} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{c.name}</span>
                    {c.specs?.length > 0 && (
                      <div className="mt-0.5 flex flex-wrap gap-x-2.5 gap-y-0.5">
                        {c.specs.map(sp => (
                          <span key={sp.name} className="flex items-center gap-1 text-[11px] text-muted-foreground/70">
                            {sp.emoji && <EmojiGlyph emoji={sp.emoji} size={12} />}
                            {sp.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <KindTag kind={c.kind} />
                  {canEdit && (
                    <>
                      <button
                        onClick={() => move(i, -1)}
                        disabled={i === 0}
                        title={T.settings.classMoveUp}
                        className={settingsIconButton}
                      >
                        <ArrowUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => move(i, 1)}
                        disabled={i === classes.length - 1}
                        title={T.settings.classMoveDown}
                        className={settingsIconButton}
                      >
                        <ArrowDown className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => startEdit(c)} className={settingsIconButton}>
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => send(`/api/match-classes/${c.id}`, 'DELETE')} className={`${settingsIconButton} hover:text-destructive`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
            {editingId === c.id && <SpecEditor specs={editSpecs} onChange={setEditSpecs} />}
            </div>
          ))}

          {classes.length === 0 && <SettingsEmpty>{T.settings.noMatchClasses}</SettingsEmpty>}
        </div>
      )}

      {error && <p className="mt-3 text-xs text-destructive">{error}</p>}

      {canEdit && (
        <div className={settingsAddRow}>
          <EmojiPicker value={newEmoji} onChange={setNewEmoji} />
          <input
            type="text"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleCreate() }}
            placeholder={T.settings.newClassName}
            className={`${settingsInput} min-w-0 flex-1`}
          />
          <KindPicker value={newKind} onChange={setNewKind} />
          <button onClick={handleCreate} disabled={!newName.trim() || busy} className={settingsAddButton}>
            <Plus className="h-3.5 w-3.5" />
            {busy ? '...' : T.settings.add}
          </button>
        </div>
      )}
    </SettingsSection>
  )
}
